import AddCircleIcon from '@mui/icons-material/AddCircle';

import React, { useContext } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { ApiContext } from '_lib/api/apiContext';
import { IsActiveEnum } from '_lib/api/apiTypes.shim';
import { ActionBar } from '_lib/components/ActionBar/ActionBar';
import { SimpleAppBar } from '_lib/components/AppBar/SimpleAppBar';
import { Button } from '_lib/components/Button/Button';
import { Divider } from '_lib/components/Divider/Divider';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';

import { Search } from '_lib/components/Search/Search';
import { activeEnumOptions, SelectFilter } from '_lib/components/SelectFilter/SelectFilter';
import { Stack } from '_lib/components/Stack/Stack';

import { normalizers, useQueryParams } from '_lib/hooks/useQueryParams';

import { AppDrawerState, AppRouteContext } from '../App/app.types';
import { AuthContext } from '../auth/authContext';
import { appRoutePaths, getFullAppRoutePath, getGoBackState } from '../Nav/nav.types';
import { PagedList } from '../PagedList/PagedList';
import { RouteErrorBoundary } from '../Routing/RouteErrorBoundary';
import { ScrollableContentWrapper } from '../ScrollableContentWrapper/ScrollableContentWrapper';
import { UserListItem } from '../User/UserListItem';

type AccountUserListProps = {
  appDrawerState?: AppDrawerState;
};

const AccountUserList = (props: AccountUserListProps) => {
  const { appDrawerState } = props;

  const navigate = useNavigate();

  const api = useContext(ApiContext);
  const { accountCacheResource, authUserCacheResource, callCenterCacheResource } = useContext(AuthContext);
  const cachedAccountQuery = accountCacheResource.useCacheQuery();
  const cachedAuthUserQuery = authUserCacheResource.useCacheQuery();
  const cachedCallCenterQuery = callCenterCacheResource.useCacheQuery();

  const accountUserDeleteMutation = api.accountUsers.useDelete();
  const accountUserReactivateMutation = api.accountUsers.useReactivate();

  const { params, setParam, searchParams } = useQueryParams({
    searchWords: { defaultValue: '' },
    isActive: { defaultValue: IsActiveEnum.True, normalize: normalizers.enum(Object.values(IsActiveEnum)) }
  });

  const accountUsersQuery = api.accountUsers.useList(
    {
      params: {
        ...params,
        accountId: cachedAccountQuery.data?.id,
        callCenterId: cachedCallCenterQuery.data?.id
      }
    },
    { enabled: !!cachedCallCenterQuery.data?.id }
  );

  if (accountUsersQuery.error) {
    return <RouteErrorBoundary appBarTitle='Account Users' errorTitle='Error Fetching Account Users' />;
  }

  return (
    <FlexBox sx={{ flexDirection: 'column', height: '100%', width: '100%' }}>
      <SimpleAppBar title='Account Users' onAppDrawerToggleClicked={appDrawerState?.openSlidingDrawer} />
      <ActionBar
        actions={
          <Button
            disableFocusRipple
            text='Add Account User'
            variant='text'
            onClick={() => {
              navigate(`${getFullAppRoutePath(appRoutePaths.AccountUser)}/new`, {
                state: { goBack: getGoBackState(appRoutePaths.AccountUser, searchParams) }
              });
            }}
            startIcon={<AddCircleIcon />}
            sx={{ textTransform: 'none' }}
          />
        }
      />
      <Divider />
      <Stack direction='row' padding={2}>
        <SelectFilter
          label='Active/Inactive'
          initialKey={params.isActive}
          options={activeEnumOptions}
          onChange={(selectedKey) => setParam('isActive', selectedKey)}
          sx={{ width: '125px' }}
        />
        <Search initialValue={params.searchWords} onSearch={(searchWords) => setParam('searchWords', searchWords)} />
      </Stack>
      <Divider />
      <ScrollableContentWrapper
        isSavingOrLoading={
          accountUsersQuery.isFetching ||
          cachedAuthUserQuery.isFetching ||
          accountUserDeleteMutation.isPending ||
          accountUserReactivateMutation.isPending
        }>
        <PagedList
          queryResult={accountUsersQuery}
          renderFunction={(accountUser) => (
            <UserListItem
              user={accountUser}
              href={`${getFullAppRoutePath(appRoutePaths.AccountUser)}/${accountUser.id}`}
              onClick={async (accountUser) => {
                navigate(`${accountUser.id}`, {
                  state: { goBack: getGoBackState(appRoutePaths.AccountUser, searchParams) }
                });
              }}
              onDelete={async (accountUser) => {
                await accountUserDeleteMutation.deleteItem({ id: accountUser.id });
              }}
              onReactivate={async (accountUser) => {
                await accountUserReactivateMutation.reactivate({ id: accountUser.id });
              }}
            />
          )}
        />
      </ScrollableContentWrapper>
    </FlexBox>
  );
};

export const AccountUserListRoute = () => {
  const { appDrawerState } = useOutletContext<AppRouteContext>();
  return <AccountUserList appDrawerState={appDrawerState} />;
};
